import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Button,
  FormGroup,
  FormControl,
  FormControlText,
  Input,
  Heading,
  InputCheckBox,
  Box,
  designTokens
} from '@itandi/itandi-bb-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSWRConfig } from 'swr';
import { useRouter } from 'next/router';
import { isSuccess } from '~/utils/api/api_handler';
import type { Staff, StaffParam } from '~/models/Staff';
import { buildDefaultStaff, createValidationSchema } from '~/models/Staff';
import { create, update } from '~/utils/api/Staff';
import { AUTH_NAME } from '~/models/StaffAuthorization';
import { generatePath } from '~/utils/generatePath';

type Props = {
  staff?: Staff | null;
};

export const FormInputPage: React.FC<Props> = ({ staff = null }: Props) => {
  const { mutate: globalMutate } = useSWRConfig();
  const router = useRouter();
  const methods = useForm<StaffParam>({
    mode: 'onBlur',
    resolver: yupResolver(createValidationSchema),
    defaultValues: staff ?? buildDefaultStaff()
  });
  const {
    formState: { errors }
  } = methods;
  const [checked, setChecked] = React.useState(
    methods.getValues(AUTH_NAME.completeStatus)
  );

  const isEditable = React.useMemo(
    () => staff === null || staff.editable,
    [staff]
  );

  const handleUpdateStatus = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (methods.watch(AUTH_NAME.completeStatus)) {
        methods.setValue(AUTH_NAME.completeStatus, false);
        setChecked(false);
      }
      methods.setValue(AUTH_NAME.status, e.target.checked, {
        shouldDirty: true
      });
    },
    [methods]
  );

  const handleUpdateAuth = React.useCallback(
    (
      value: (typeof AUTH_NAME)[keyof typeof AUTH_NAME],
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      methods.setValue(value, e.target.checked, { shouldDirty: true });
    },
    [methods]
  );

  return (
    <Box width={360}>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(async (submitValue: StaffParam) => {
            const response =
              staff === null
                ? await create(submitValue)
                : await update(submitValue, staff.id);
            if (isSuccess(response)) {
              if (staff !== null) {
                globalMutate(generatePath('/staffs/:id', { id: staff.id }));
              }
              router.push('/settings/staffs');
              toast.success(staff === null ? '追加しました' : '更新しました');
              return;
            }
            toast.error('エラーが発生しました');
          })}
        >
          <Heading as="h3" marginBottom={designTokens['spacing/xs']}>
            詳細
          </Heading>
          <FormGroup
            marginBottom={designTokens['spacing/m']}
            variant="Vertical"
          >
            <FormControl
              chip="必須"
              isInvalid={errors.name != null}
              label="名前"
            >
              <Input
                disabled={!isEditable}
                isInvalid={errors.name != null}
                placeholder="例）内装 太郎"
                {...methods.register('name')}
              />
              <FormControlText error={errors.name?.message} />
            </FormControl>
            <FormControl
              chip="必須"
              isInvalid={errors.email != null}
              label="メールアドレス"
            >
              <Input
                disabled={!isEditable}
                isInvalid={errors.email != null}
                placeholder="例）itandi.tarou@example.com"
                {...methods.register('email')}
              />
              <FormControlText error={errors.email?.message} />
            </FormControl>
            <FormControl
              isInvalid={errors.phoneNumber != null}
              label="電話番号"
            >
              <Input
                disabled={staff !== null && staff.authUserId !== null}
                isInvalid={errors.phoneNumber != null}
                placeholder="例）070-1234-5678"
                {...methods.register('phoneNumber')}
              />
              <FormControlText error={errors.phoneNumber?.message} />
            </FormControl>
          </FormGroup>
          <Heading as="h3" marginBottom={designTokens['spacing/xs']}>
            権限
          </Heading>
          <FormGroup variant="Vertical">
            <FormControl label="設定画面へのアクセス">
              <InputCheckBox
                checked={methods.watch(AUTH_NAME.setting)}
                label="設定画面へのアクセスを許可"
                onChange={(e): void => handleUpdateAuth(AUTH_NAME.setting, e)}
              />
            </FormControl>
            <FormControl label="案件のステータス移行">
              <InputCheckBox
                checked={methods.watch(AUTH_NAME.status)}
                label="案件のステータス移行を許可"
                onChange={handleUpdateStatus}
              />
            </FormControl>
            <FormControl label="案件のステータスを完了へ移行">
              <InputCheckBox
                checked={checked}
                disabled={!methods.watch(AUTH_NAME.status)}
                label="案件の完了ステータスへの移行を許可"
                onChange={(e): void => {
                  handleUpdateAuth(AUTH_NAME.completeStatus, e);
                  setChecked(e.target.checked);
                }}
              />
            </FormControl>
            <FormControl label="案件の取り下げ・削除">
              <InputCheckBox
                checked={methods.watch(AUTH_NAME.cancel)}
                label="案件の取り下げ・削除を許可"
                onChange={(e): void => handleUpdateAuth(AUTH_NAME.cancel, e)}
              />
            </FormControl>
          </FormGroup>
          <Button
            disabled={!methods.formState.isDirty}
            loading={methods.formState.isSubmitting}
            marginTop={designTokens['spacing/m']}
            type="submit"
            variant="primary"
          >
            {staff === null ? '追加' : '更新'}
          </Button>
        </form>
      </FormProvider>
    </Box>
  );
};
